import { FormControl, InputLabel, Select, SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import type { ReactNode } from 'react';

interface StyledSelectProps {
  sx?: SxProps;
  value?: string | null;
  label?: string;
  shrink?: boolean;
  children: ReactNode;
  disabled?: boolean;
  disabledOnDark?: boolean;
  onChange: (value: string) => void;
}

export default function StyledSelect({
  sx,
  value,
  onChange,
  label,
  shrink = true,
  children,
  disabled = false,
  disabledOnDark = false
}: StyledSelectProps) {
  const theme = useTheme();

  return (
    <FormControl size="small" variant="outlined">
      <InputLabel
        id={label}
        shrink={shrink}
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '14px',
          fontWeight: 500,
          transform: 'translate(16px, 12px) scale(1)',
          '&.MuiInputLabel-shrunk': {
            transform: 'translate(14px, -9px) scale(0.75)'
          },
          '&.Mui-focused': {
            color: theme.palette.primary.main
          }
        }}>
        {label}
      </InputLabel>
      <Select
        labelId={label}
        label={label}
        value={value}
        disabled={disabled}
        IconComponent={KeyboardArrowDownIcon}
        onChange={(e) => onChange(e.target.value ?? '')}
        sx={{
          backgroundColor: disabled
            ? disabledOnDark
              ? '#bdbdbd'
              : theme.palette.action.disabledBackground
            : theme.palette.background.default,
          color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
          minWidth: '12em',
          height: '48px', // Fixed height for consistency
          borderRadius: '8px',
          transition: 'all 0.2s ease-in-out',
          boxShadow: disabled ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.08)',
          '&:hover': disabled
            ? {}
            : {
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px'
                }
              },
          '&.Mui-focused': disabled
            ? {}
            : {
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.primary.main,
                  borderWidth: '2px'
                }
              },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: disabled ? 'rgba(0, 0, 0, 0.06)' : 'rgba(0, 0, 0, 0.12)',
            borderWidth: '1px'
          },
          '&.Mui-disabled': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.06)'
            }
          },
          '& .MuiSelect-select': {
            padding: '12px 16px',
            fontSize: '14px',
            fontWeight: 400,
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            minHeight: '20px',
            lineHeight: '20px',
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              WebkitTextFillColor: theme.palette.text.disabled
            }
          },
          '& .MuiSvgIcon-root': {
            fontSize: '20px',
            color: disabled ? theme.palette.action.disabled : theme.palette.text.secondary,
            transition: 'all 0.2s ease-in-out',
            right: '12px'
          },
          '&:hover .MuiSvgIcon-root': disabled
            ? {}
            : {
                color: theme.palette.primary.main,
                transform: 'rotate(180deg)'
              },
          '&.Mui-focused .MuiSvgIcon-root': disabled
            ? {}
            : {
                color: theme.palette.primary.main,
                transform: 'rotate(180deg)'
              },
          ...sx
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              marginTop: '4px',
              '& .MuiMenuItem-root': {
                fontSize: '14px',
                padding: '10px 16px',
                transition: 'background-color 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}10`
                },
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.primary.main}20`,
                  '&:hover': {
                    backgroundColor: `${theme.palette.primary.main}30`
                  }
                }
              }
            }
          }
        }}>
        {children}
      </Select>
    </FormControl>
  );
}
